import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Productpost from './API/Productpost';
import { useCart } from './context/cart';
import Button_green from './UI/Button_green/Button_green';
import Loader from './UI/Loader/Loader';
import useScreenSize from '../hooks/useScreenSize';
import IprokWindow from './Iprok';


const fallbackStyles = {
  wrap: {
    fontFamily: 'Inter, Arial, sans-serif',
    background: '#fff',
    borderRadius: 12,
    gridColumn: '1 / -1',
  },
  mainImg: {
    width: '100%',
    height: 590,
    objectFit: 'cover',
    borderRadius: 10
  },
  thumbImg: {
    width: '100%',
    height: 110,
    objectFit: 'cover',
    borderRadius: 8
  },
  badge: {
    display: 'inline-block',
    background: '#f3f8f6',
    color: '#2e8b57',
    padding: '6px 10px',
    borderRadius: 8,
    fontSize: 13,
    border: '1px solid rgba(46,139,87,0.08)',
    marginRight: 8,
    marginBottom: 8
  }
};

const ProductPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const { isMobile, isTablet } = useScreenSize();
    
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [error, setError] = useState('');

    // Расчет площади и цены
    const calculateArea = () => {
        return parseFloat((width * height).toFixed(2));
    };

    const calculateTotalPrice = () => {
        if (!product) return 0;
        const basePrice = parseFloat(product.price);
        const area = calculateArea();
        return parseFloat((area * basePrice).toFixed(2));
    };

    useEffect(() => {
        fetchProduct();
    }, [id]);

    const fetchProduct = async () => {
        try {
            setLoading(true);
            const response = await Productpost.getProduct(id);
            if (response && response.data && response.data.success) {
                setProduct(response.data.data);
            } else {
                setError('Товар не знайдено');
            }
        } catch (error) {
            console.error('Error fetching product:', error);
            setError('Помилка завантаження товару');
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = () => {
        const area = calculateArea();
        if (area < 1) {
            alert('Мінімальна площа 1 м²');
            return;
        }
        const sizeString = `${width} x ${height} м (${area} м²)`;
        const totalPrice = calculateTotalPrice();
        
        // Создаем копию продукта с рассчитанной ценой
        const productWithCalculatedPrice = {
            ...product,
            price: totalPrice
        };
        
        addToCart(productWithCalculatedPrice, sizeString, quantity);
        navigate('/Cart');
    };

    const getMediaItems = () => {
        const items = [];
        
        if (product.images) {
            try {
                const images = JSON.parse(product.images);
                images.forEach(img => items.push({ type: 'image', src: img }));
            } catch (e) {
                console.error('Error parsing images:', e);
            }
        }
        
        if (product.videos) {
            try {
                const videos = JSON.parse(product.videos);
                videos.forEach(video => items.push({ type: 'video', src: video }));
            } catch (e) {
                console.error('Error parsing videos:', e);
            }
        }
        
        return items;
    };

    const renderMediaCarousel = () => {
        const mediaItems = getMediaItems();
        if (mediaItems.length === 0) return null;

        const currentItem = mediaItems[currentMediaIndex];

        return (
            <div className="product-media">
                <div className="media-main">
                    {currentItem.type === 'image' ? (
                        <img 
                            src={currentItem.src} 
                            alt={product.name}
                            style={{ width: '100%', maxHeight: '450px', objectFit: 'cover', borderRadius: 10 }}
                        />
                    ) : (
                        <video 
                            controls 
                            style={{ width: '100%', maxHeight: '450px' }}
                        >
                            <source src={currentItem.src} type="video/mp4" />
                            Ваш браузер не поддерживает видео.
                        </video>
                    )}
                </div>
                
                {mediaItems.length > 1 && (
                    <div className="media-thumbs" style={{ display: 'flex', gap: '10px', marginTop: '10px', overflowX: 'auto' }}>
                        {mediaItems.map((item, index) => (
                            <div
                                key={index}
                                onClick={() => setCurrentMediaIndex(index)}
                                style={{
                                    cursor: 'pointer',
                                    border: index === currentMediaIndex ? '2px solid #4CAF50' : '1px solid #ddd',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    minWidth: '80px',
                                    height: '60px'
                                }}
                            >
                                {item.type === 'image' ? (
                                    <img 
                                        src={item.src} 
                                        alt=""
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        backgroundColor: '#333',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontSize: '12px'
                                    }}>
                                        VIDEO
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (loading) return <Loader />;
    if (error) return <div className="error-message" style={{ fontFamily: 'Inter, sans-serif' }}>{error}</div>;
    if (!product) return <div style={{ fontFamily: 'Inter, sans-serif' }}>Товар не знайдено</div>;

    return (
        <div className="product-page" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
            <div className="product-content" style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile || isTablet ? '1fr' : '1fr 1fr', 
                gap: isMobile ? '20px' : '0px 40px', 
                alignItems: 'start' 
            }}>
                <div className="product-media-container">
                    {renderMediaCarousel()}
                </div>

                <div className="product-info">
                    <h1 style={{ fontSize: '28px', marginBottom: '10px', fontFamily: 'Inter, sans-serif' }}>{product.name}</h1>
                    <p className="price" style={{ fontSize: '24px', color: '#4CAF50', fontWeight: 'bold', marginBottom: '10px', fontFamily: 'Inter, sans-serif' }}>
                        {parseFloat(product.price).toLocaleString()} ₴/м²
                    </p>

                    {product.description && (
                        <div style={{ marginBottom: '20px' }}>
                            <p style={{ fontFamily: 'Inter, sans-serif' }}>{product.description}</p>
                        </div>
                    )}

                    <div className="flex flex-wrap mb-3">
                        <div style={fallbackStyles.badge}>🔒 Запатентовано</div>
                        <div style={fallbackStyles.badge}>🔬 Пройдено випробування</div>
                        <div style={fallbackStyles.badge}>🛠️ Модульний та швидкий монтаж</div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <br/>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                                    Ширина (м):
                                </label>
                                <input 
                                    step="0.1"
                                    min="0"
                                    value={width} 
                                    onChange={(e) => setWidth(e.target.value)}
                                    style={{
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                        width: '100%',
                                        fontFamily: 'Inter, sans-serif'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                                    Висота (м):
                                </label>
                                <input 
                                    min="0"
                                    value={height} 
                                    onChange={(e) => setHeight(e.target.value)}
                                    style={{
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                        width: '100%',
                                        fontFamily: 'Inter, sans-serif'
                                    }}
                                />
                            </div>
                        </div>
                        
                        {/* Отображение расчетов */}
                        <div style={{ 
                            background: '#f8f9fa', 
                            padding: '15px', 
                            borderRadius: '8px',
                            border: '1px solid #e9ecef'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Площа:</span>
                                <strong style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                                    {calculateArea()} м²
                                </strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Ціна за м²:</span>
                                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                                    {product ? parseFloat(product.price).toLocaleString() : 0} ₴
                                </span>
                            </div>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                paddingTop: '8px',
                                borderTop: '1px solid #dee2e6'
                            }}>
                                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', fontWeight: 'bold' }}>
                                    Загальна вартість:
                                </span>
                                <strong style={{ 
                                    fontFamily: 'Inter, sans-serif', 
                                    fontSize: '18px', 
                                    color: '#4CAF50' 
                                }}>
                                    {calculateTotalPrice().toLocaleString()} ₴
                                </strong>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <h3 style={{ fontFamily: 'Inter, sans-serif' }}>Кількість:</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <button 
                                onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                                style={{
                                    padding: '8px 12px',
                                    border: '1px solid #ddd',
                                    background: 'white',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontFamily: 'Inter, sans-serif'
                                }}
                            >
                                -
                            </button>
                            <span style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'Inter, sans-serif' }}>
                                {quantity}
                            </span>
                            <button 
                                onClick={() => setQuantity(quantity + 1)}
                                style={{
                                    padding: '8px 12px',
                                    border: '1px solid #ddd',
                                    background: 'white',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontFamily: 'Inter, sans-serif'
                                }}
                            >
                                +
                            </button>
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <Button_green onClick={handleAddToCart}>
                            ДОДАТИ В КОШИК
                        </Button_green>
                    </div>
                </div>
                <IprokWindow
                    images={['/img/prok1.jpg','/img/prok2.jpg','/img/prok3.jpg','/img/prok4.jpg']}
                    video={'/img/videos/prok1.mp4'}
                    fallbackStyles={fallbackStyles}
                />
            </div>
        </div>
    );
};

export default ProductPage;